import {Menu} from '@mantine/core';
import type {
  MRT_ColumnFiltersState,
  MRT_SortingState,
} from 'mantine-react-table';
import React from 'react';
import type {UserGroupSimpleResponse} from 'src/api/ProactApiClient';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useGetUserGroupListV2Query} from 'src/redux/queries/userGroup';
import {useUserGroupColumns} from './hooks/useUserGroupColumns';

export interface UserGroupProps {
  identifier: string;
  defaultSorting?: MRT_SortingState;
  defaultColumnFilters?: MRT_ColumnFiltersState;

  actionBarPreNode?: React.ReactNode;
  actionBarPostNode?: React.ReactNode;

  onViewRowClick: (row: UserGroupSimpleResponse) => void;
}

export const UserGroupTable: React.FC<UserGroupProps> = ({
  identifier,
  defaultSorting,
  defaultColumnFilters,
  actionBarPreNode,
  actionBarPostNode,
  onViewRowClick,
}) => {
  const columns = useUserGroupColumns();

  const tableState = useTableState({
    identifier: identifier,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: defaultSorting ?? [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
    defaultColumnFilters: defaultColumnFilters ?? [],
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const params = {
    start: start,
    length: length,
    filters: filters,
    filterFns: filterFns,
    sorting: sorting,
  };
  const {data, isLoading, isError, isFetching, refetch} =
    useGetUserGroupListV2Query(params, {refetchOnMountOrArgChange: true});

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActionMenuItems: ({row}) => (
        <>
          <Menu.Item onClick={() => onViewRowClick(row.original)}>
            View
          </Menu.Item>
        </>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {actionBarPreNode}
            {actionBarPostNode}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
